import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from '@emotion/styled';

// Container for markdown content
const MarkdownContainer = styled.div`
  color: var(--color-text);
  font-family: var(--font-body);
  font-size: var(--font-size-base);
  line-height: 1.7;
  
  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
    font-weight: var(--font-weight-bold);
    margin-top: 1.5em;
    margin-bottom: 0.75em;
    color: var(--color-text);
    line-height: 1.3;
  }
  
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1.1em; }
  h5 { font-size: 1em; }
  h6 { font-size: 0.9em; }
  
  /* First heading shouldn't have top margin */
  > h1:first-child,
  > h2:first-child,
  > h3:first-child {
    margin-top: 0;
  }
  
  /* Paragraphs */
  p {
    margin: 0 0 1em 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Lists */
  ul, ol {
    margin: 0 0 1em 0;
    padding-left: 1.5em;
    
    li {
      margin: 0.25em 0;
      line-height: 1.6;
    }
    
    ul, ol {
      margin: 0.25em 0 0.25em 0;
    }
  }
  
  /* Links */
  a {
    color: var(--color-primary);
    text-decoration: none;
    transition: all 0.2s ease;
    border-bottom: 1px solid transparent;
    
    &:hover {
      color: var(--color-accent);
      border-bottom-color: var(--color-accent);
    }
  }
  
  /* Code */
  code {
    font-family: var(--font-mono);
    font-size: 0.9em;
    padding: 0.2em 0.4em;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-primary);
  }
  
  /* Code blocks */
  pre {
    margin: 1em 0;
    padding: 1em;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    overflow-x: auto;
    position: relative;
    
    code {
      font-family: var(--font-mono);
      font-size: 0.9em;
      padding: 0;
      background: transparent;
      border: none;
      color: var(--color-text);
    }
  }
  
  /* Blockquotes */
  blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    border-left: 4px solid var(--color-primary);
    background: var(--color-surface);
    border-radius: var(--radius-sm);
    
    p {
      margin: 0.5em 0;
    }
  }
  
  /* Tables */
  table {
    width: 100%;
    margin: 1em 0;
    border-collapse: collapse;
    font-size: 0.95em;
  }
  
  th, td {
    padding: 0.75em;
    text-align: left;
    border: 1px solid var(--color-border);
  }
  
  th {
    background: var(--color-surface);
    font-weight: var(--font-weight-bold);
  }
  
  tbody tr:hover {
    background: var(--color-surface);
  }
  
  /* Horizontal rules */
  hr {
    margin: 2em 0;
    border: none;
    border-top: 1px solid var(--color-border);
  }
  
  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-md);
  }
  
  /* Strong and emphasis */
  strong {
    font-weight: var(--font-weight-bold);
    color: var(--color-text);
  }
  
  em {
    font-style: italic;
  }
  
  /* Strikethrough */
  del {
    text-decoration: line-through;
    opacity: 0.7;
  }
`;

// Copy button for code blocks
const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-family: var(--font-body);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  pre:hover & {
    opacity: 1;
  }
  
  &:hover {
    background: var(--color-secondary);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Code block wrapper with copy functionality
const CodeBlockWrapper = styled.div`
  position: relative;
  
  &:hover ${CopyButton} {
    opacity: 1;
  }
`;

interface MarkdownContentProps {
  content: string;
  className?: string;
  disableLinks?: boolean;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className, disableLinks = false }) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Custom component for code blocks with copy button and external links
  const components = {
    pre: ({ children, ...props }: any) => {
      const codeContent = children?.props?.children || '';
      const isCopied = copiedCode === codeContent;
      
      return (
        <CodeBlockWrapper>
          <pre {...props}>{children}</pre>
          <CopyButton
            onClick={() => handleCopyCode(codeContent)}
          >
            {isCopied ? 'Copied!' : 'Copy'}
          </CopyButton>
        </CodeBlockWrapper>
      );
    },
    a: ({ href, children, ...props }: any) => {
      // If links are disabled, just render the text
      if (disableLinks) {
        return <span {...props}>{children}</span>;
      }
      
      // Check if it's an external link
      const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
      
      return (
        <a 
          href={href} 
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          {...props}
        >
          {children}
          {isExternal && ' ↗'}
        </a>
      );
    },
  };

  return (
    <MarkdownContainer className={className}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </MarkdownContainer>
  );
};